import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Lock, Mail, Eye, EyeOff, Store, Loader2, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { Button, Input } from '../components/ui';
import PasswordResetPromptModal from '../components/auth/PasswordResetPromptModal';
import {
  isGoogleAccountNoPasswordError,
  sanitizeAuthErrorMessage,
  saveGooglePasswordPrompt,
  loadGooglePasswordPrompt,
  clearGooglePasswordPrompt,
} from '../utils/authErrors';

export const GOOGLE_PASSWORD_PROMPT_EVENT = 'seller:google-password-prompt';

export function notifyGooglePasswordPrompt(email: string) {
  saveGooglePasswordPrompt(email);
  window.dispatchEvent(new CustomEvent(GOOGLE_PASSWORD_PROMPT_EVENT, { detail: { email } }));
}

const Login: React.FC = () => {
  const { t } = useTranslation('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
  const [showGoogleAccountAlert, setShowGoogleAccountAlert] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const buyerSiteUrl =
    process.env.REACT_APP_BUYER_URL?.replace(/\/+$/, '') || 'https://market.st-cael.org';

  const { completeSession } = useAuth();
  const navigate = useNavigate();

  const openGooglePasswordPrompt = useCallback((accountEmail: string) => {
    setShowGoogleAccountAlert(true);
    setShowPasswordResetModal(true);
    setError('');
    setEmailNotVerified(false);
    notifyGooglePasswordPrompt(accountEmail);
  }, []);

  useEffect(() => {
    const pendingEmail = loadGooglePasswordPrompt();
    if (pendingEmail) {
      setEmail(pendingEmail);
      setShowGoogleAccountAlert(true);
      setShowPasswordResetModal(true);
    }

    const onPrompt = (event: Event) => {
      const detail = (event as CustomEvent<{ email?: string }>).detail;
      const accountEmail = detail?.email || loadGooglePasswordPrompt();
      if (accountEmail) {
        setEmail(accountEmail);
        setShowGoogleAccountAlert(true);
        setShowPasswordResetModal(true);
        setError('');
      }
    };

    window.addEventListener(GOOGLE_PASSWORD_PROMPT_EVENT, onPrompt);
    return () => window.removeEventListener(GOOGLE_PASSWORD_PROMPT_EVENT, onPrompt);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('errors.requiredFields'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/auth/login', { email, password, type: 'seller' });
      const data = response.data?.data ?? response.data;
      const { user: userData, token: tokenData } = data;

      if (!tokenData || !userData) {
        setError(t('errors.loginFailed'));
        return;
      }

      clearGooglePasswordPrompt();
      await completeSession(tokenData, userData);
    } catch (err: any) {
      if (isGoogleAccountNoPasswordError(err) || err.message === 'NO_PASSWORD') {
        openGooglePasswordPrompt(email);
        return;
      }

      if (
        err.response?.status === 403 &&
        err.response?.data?.message?.includes?.('verified')
      ) {
        setEmailNotVerified(true);
        setShowPasswordResetModal(false);
        setShowGoogleAccountAlert(false);
        setError(
          t('errors.emailNotVerified', {
            defaultValue:
              'Your email is not verified. Please check your inbox for the verification email.',
          })
        );
        return;
      }

      const rawMessage =
        typeof err.response?.data === 'object'
          ? err.response?.data?.message
          : undefined;
      const message = sanitizeAuthErrorMessage(rawMessage || err.message);
      setError(message || t('errors.loginFailed'));
      setEmailNotVerified(false);
      setShowPasswordResetModal(false);
      setShowGoogleAccountAlert(false);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPasswordReset = () => {
    setShowPasswordResetModal(false);
    clearGooglePasswordPrompt();
    navigate('/forgot-password', { state: { email, autoSend: true } });
  };

  const handleCloseModal = () => {
    setShowPasswordResetModal(false);
    // Keep sessionStorage + inline alert so user still sees guidance
  };

  const handleGooglePasswordSetup = () => {
    clearGooglePasswordPrompt();
    navigate('/forgot-password', { state: { email } });
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError(t('errors.emailRequired', { defaultValue: 'Please enter your email address' }));
      return;
    }

    setIsResending(true);
    setError('');
    try {
      const response = await api.post('/auth/resend-verification', { email });
      if (response.data.status === 'success') {
        setError('');
        alert(
          t('verificationEmailSent', {
            defaultValue: 'Verification email sent! Please check your inbox.',
          })
        );
        setEmailNotVerified(false);
      } else {
        setError(
          response.data.message ||
            t('errors.resendFailed', { defaultValue: 'Failed to resend verification email.' })
        );
      }
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          t('errors.resendFailed', { defaultValue: 'Failed to resend verification email.' })
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <>
      <PasswordResetPromptModal
        open={showPasswordResetModal}
        email={email}
        onClose={handleCloseModal}
        onConfirm={handleConfirmPasswordReset}
      />

      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <Store className="text-custom-primary" size={48} />
            </div>
            <h1 className="text-4xl font-bold text-custom-primary mb-2">{t('title')}</h1>
            <p className="text-gray-600">{t('subtitle')}</p>
          </div>

          <div className="card p-8">
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              ) : null}

              <Input
                label={t('fields.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('fields.emailPlaceholder')}
                icon={Mail}
                iconPosition="left"
                required
              />

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password', { state: { email } })}
                  className="text-sm text-custom-primary hover:underline font-medium"
                >
                  {t('buttons.forgotPassword')}
                </button>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.password')}
                </label>
                <div className="relative">
                  <Lock
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                    size={20}
                  />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12 pr-12"
                    placeholder={t('fields.passwordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <Button type="submit" variant="primary" className="w-full" loading={loading}>
                {loading ? t('buttons.signingIn') : t('buttons.signIn')}
              </Button>

              {showGoogleAccountAlert ? (
                <div
                  className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 space-y-3"
                  role="alert"
                >
                  <div className="flex gap-3">
                    <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={22} />
                    <div className="space-y-2">
                      <p className="font-semibold text-amber-950 text-sm">
                        {t('noPassword.inlineAlert.title')}
                      </p>
                      <p className="text-sm text-amber-900 leading-relaxed">
                        {t('noPassword.inlineAlert.message')}
                      </p>
                      {email ? (
                        <p className="text-xs text-amber-800">
                          {t('noPassword.modal.accountLabel')}{' '}
                          <strong>{email}</strong>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="primary"
                    className="w-full"
                    onClick={handleGooglePasswordSetup}
                  >
                    {t('noPassword.inlineAlert.action')}
                  </Button>
                </div>
              ) : null}

              {emailNotVerified ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <p className="text-yellow-800 text-sm mb-3">
                    {t('emailNotVerified', {
                      defaultValue:
                        'Your email is not verified. Please check your inbox for the verification email.',
                    })}
                  </p>
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={isResending}
                    className="w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-900 font-medium py-2 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isResending ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {t('resending', { defaultValue: 'Sending...' })}
                      </>
                    ) : (
                      t('resendVerification', { defaultValue: 'Resend Verification Email' })
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/verify-email', { state: { email, fromLogin: true } })}
                    className="w-full mt-2 text-yellow-800 hover:text-yellow-900 text-sm underline"
                  >
                    {t('goToVerification', { defaultValue: 'Go to Verification Page' })}
                  </button>
                </div>
              ) : null}

              <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 text-sm text-blue-900 space-y-2">
                <p className="font-medium">{t('newSeller.title')}</p>
                <ol className="list-decimal list-inside space-y-1 text-blue-800">
                  <li>{t('newSeller.step1')}</li>
                  <li>{t('newSeller.step2')}</li>
                  <li>{t('newSeller.step3')}</li>
                </ol>
                <a
                  href={buyerSiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-custom-primary hover:underline font-medium"
                >
                  {t('newSeller.link')} →
                </a>
              </div>

              <div className="text-center">
                <p className="text-xs text-gray-500">{t('newSeller.note')}</p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
